import PropTypes from 'prop-types';
import {
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TablePagination,
    Stack,
    Typography,
    Tooltip,
    IconButton,
    Chip,
    CircularProgress,
    Box,
    Button
} from '@mui/material';
import { ReceiptLongOutlined, ContentCopyOutlined, CalendarMonthOutlined, EmailOutlined } from '@mui/icons-material';
import moment from 'moment';

import { globalNotifySuccess } from 'views/utilities/AlertToast';
import { formatINR } from '../credit-limits/creditLimitsUtils';

const renderStatusBadge = (status) => {
    const s = String(status || 'SUCCESS').toUpperCase();
    let bgcolor = '#f0fdf4';
    let color = '#15803d';
    let border = '1px solid #bbf7d0';

    if (['PENDING', 'PROCESSING', 'IN_PROGRESS'].includes(s)) {
        bgcolor = '#fffbeb';
        color = '#b45309';
        border = '1px solid #fde68a';
    } else if (['FAILED', 'FAILURE', 'REJECTED'].includes(s)) {
        bgcolor = '#fef2f2';
        color = '#b91c1c';
        border = '1px solid #fecaca';
    }

    return (
        <Chip
            label={s}
            size="small"
            sx={{
                height: 22,
                fontSize: '0.7rem',
                fontWeight: 700,
                bgcolor,
                color,
                border
            }}
        />
    );
};

const TransactionHistoryTable = ({
    loading = false,
    transactions = [],
    totalCount = 0,
    page = 0,
    rowsPerPage = 20,
    onPageChange,
    onRowsPerPageChange,
    fromDate,
    toDate,
    onResetFilter
}) => {
    return (
        <>
            <TableContainer sx={{ minHeight: 400 }}>
                <Table sx={{ minWidth: 800 }}>
                    <TableHead sx={{ bgcolor: '#f8fafc' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', py: 1.8 }}>ORDER ID</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary' }}>DATE & TIME</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary' }}>EMAIL ID</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right' }}>
                                AMOUNT (₹)
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary' }}>REFERENCE / RRN</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary' }}>STATUS</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={6} sx={{ py: 10, textAlign: 'center' }}>
                                    <CircularProgress size={36} sx={{ color: '#1e40af' }} />
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                        Loading transaction history...
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : transactions.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} sx={{ py: 10, textAlign: 'center' }}>
                                    <Box
                                        sx={{
                                            width: 64,
                                            height: 64,
                                            borderRadius: '50%',
                                            bgcolor: '#f1f5f9',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            margin: '0 auto',
                                            color: '#94a3b8',
                                            mb: 2
                                        }}
                                    >
                                        <ReceiptLongOutlined sx={{ fontSize: 32 }} />
                                    </Box>
                                    <Typography variant="h6" fontWeight={700} color="text.primary">
                                        No Transactions Found
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mx: 'auto', mt: 0.5 }}>
                                        No credit repayment transactions were recorded between <strong>{fromDate}</strong> and{' '}
                                        <strong>{toDate}</strong>. Try modifying the date range or clearing your search.
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={onResetFilter}
                                        sx={{ mt: 2, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                                    >
                                        Reset Filters
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ) : (
                            transactions.map((item, index) => {
                                const orderId = item.order_id || 'N/A';
                                const amountVal = Number(item.paid_amount ?? item.amount ?? 0);
                                const dateVal = item.settled_at || item.created_at;
                                const formattedDate =
                                    dateVal && moment(dateVal).isValid()
                                        ? moment(dateVal).format('DD MMM YYYY, hh:mm A')
                                        : dateVal || 'N/A';

                                return (
                                    <TableRow
                                        key={item.order_id || index}
                                        hover
                                        sx={{
                                            '&:last-child td, &:last-child th': { border: 0 }
                                        }}
                                    >
                                        {/* Order ID */}
                                        <TableCell sx={{ py: 1.8 }}>
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={700}
                                                    sx={{ color: '#1e40af', fontFamily: 'monospace' }}
                                                >
                                                    {orderId}
                                                </Typography>
                                                {orderId !== 'N/A' && (
                                                    <Tooltip title="Copy Order ID">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigator.clipboard.writeText(String(orderId));
                                                                globalNotifySuccess('Order ID copied');
                                                            }}
                                                            sx={{ p: 0.3 }}
                                                        >
                                                            <ContentCopyOutlined sx={{ fontSize: 14, color: 'text.disabled' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </TableCell>

                                        {/* Date & Time */}
                                        <TableCell>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <CalendarMonthOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                <Typography variant="body2" color="text.primary" fontSize="0.82rem">
                                                    {formattedDate}
                                                </Typography>
                                            </Stack>
                                        </TableCell>

                                        {/* Email ID */}
                                        <TableCell>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <EmailOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                <Typography variant="body2" fontWeight={600} color="text.primary">
                                                    {item.email || 'N/A'}
                                                </Typography>
                                            </Stack>
                                        </TableCell>

                                        {/* Amount */}
                                        <TableCell sx={{ textAlign: 'right' }}>
                                            <Typography variant="body2" fontWeight={800} color="#0f766e" fontSize="0.95rem">
                                                {formatINR(amountVal)}
                                            </Typography>
                                        </TableCell>

                                        {/* Reference / RRN */}
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary" fontSize="0.82rem">
                                                {item.rrn || 'N/A'}
                                            </Typography>
                                        </TableCell>

                                        {/* Status */}
                                        <TableCell>{renderStatusBadge(item.status)}</TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
                rowsPerPageOptions={[10, 20, 50, 100]}
                component="div"
                count={totalCount || transactions.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, newPage) => onPageChange(newPage)}
                onRowsPerPageChange={(e) => {
                    const newPerPage = parseInt(e.target.value, 10);
                    onRowsPerPageChange(newPerPage);
                }}
                sx={{
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    bgcolor: '#fff'
                }}
            />
        </>
    );
};

TransactionHistoryTable.propTypes = {
    loading: PropTypes.bool,
    transactions: PropTypes.arrayOf(PropTypes.object),
    totalCount: PropTypes.number,
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
    onRowsPerPageChange: PropTypes.func.isRequired,
    fromDate: PropTypes.string.isRequired,
    toDate: PropTypes.string.isRequired,
    onResetFilter: PropTypes.func.isRequired
};

export default TransactionHistoryTable;
